import {
  buildStudioHumanizationComparison,
  buildStudioImprovementHints,
  type StudioHumanizationComparison,
  type StudioHumanizationSnapshot,
  type StudioImprovementHint,
} from "./improvementHints";
import type { StudioPreviewEvidenceEntry } from "./studioPreviewEvidenceLedger";

export const STUDIO_IMPROVE_LOOP_VERSION = "a22.7_v1" as const;

export type StudioImproveQaStatus = "pass" | "warn" | "fail";
export type StudioImproveSeverity = "review" | "blocked";
export type StudioImprovePriorityLane =
  | "start_with"
  | "monitor_next"
  | "blocked";
export type StudioImproveSuggestionArea =
  | "prompt"
  | "tone"
  | "objection"
  | "close";
export type StudioImproveReviewState =
  | "pending"
  | "applied"
  | "rejected"
  | "blocked";

export type StudioImproveInteraction = {
  id: string;
  sourceLabel: string;
  channel: string;
  stage: string;
  customerExcerpt: string;
  assistantExcerpt: string;
  qaStatus: StudioImproveQaStatus;
  qaLabel: string;
  flaggedFriction: string[];
};

export type StudioImproveFriction = {
  id: string;
  label: string;
  detail: string;
  severity: StudioImproveSeverity;
  interactionLabel: string;
  priorityLane: StudioImprovePriorityLane;
  priorityRank: number;
};

export type StudioImproveSuggestion = {
  id: string;
  area: StudioImproveSuggestionArea;
  title: string;
  summary: string;
  draftChange: string;
  rationale: string;
  reviewState: StudioImproveReviewState;
  blockedReason?: string;
  sourceLabels: string[];
  priorityLane: StudioImprovePriorityLane;
  priorityRank: number;
  priorityLabel: string;
};

export type StudioImproveLoopReadModel = {
  version: typeof STUDIO_IMPROVE_LOOP_VERSION;
  deterministic: true;
  title: string;
  summary: string;
  notes: string[];
  evidenceEntries: StudioPreviewEvidenceEntry[];
  humanization: {
    currentPreview: StudioHumanizationSnapshot;
    tunedDraft: StudioHumanizationSnapshot;
    comparison: StudioHumanizationComparison;
    tuningRecommendations: StudioImprovementHint[];
  };
  recentInteractions: StudioImproveInteraction[];
  flaggedFriction: StudioImproveFriction[];
  suggestedEdits: StudioImproveSuggestion[];
  reviewPriority: {
    startWithSummary: string;
    monitorNextSummary: string;
  };
};

type StudioEvidenceVoiceSnapshot = {
  entry: StudioPreviewEvidenceEntry;
  snapshot: StudioHumanizationSnapshot;
};

const SCENARIO_OPERATIONAL_STAGE_LABELS = {
  payment_not_ready: "payment readiness",
  distance_timing_hesitation: "distance/timing",
  renewal_clarification: "renewal clarification",
  transfer_intake_clarification: "transfer intake clarification",
  veteran_funding_boundary: "veteran/funding boundary",
  distance_location_renewal_transfer: "distance/location",
  callback_rescue: "callback rescue",
  transfer_payment_fallback: "transfer/payment fallback",
} as const;

type StudioOperationalFrictionTag =
  | "payment readiness"
  | "distance/timing"
  | "renewal clarification"
  | "transfer intake clarification"
  | "veteran/funding boundary"
  | "distance/location"
  | "callback rescue"
  | "transfer/payment fallback";

type StudioImprovePriorityTag =
  | StudioOperationalFrictionTag
  | "warmth"
  | "repetition"
  | "professionalism"
  | "close quality"
  | "concision"
  | "unsafe scope"
  | "preview blocked"
  | "no match";

const STUDIO_IMPROVE_PRIORITY_CONFIG: Record<
  StudioImprovePriorityTag,
  {
    lane: StudioImprovePriorityLane;
    rank: number;
    label: string;
  }
> = {
  "callback rescue": {
    lane: "start_with",
    rank: 10,
    label: "Callback rescue",
  },
  "transfer/payment fallback": {
    lane: "start_with",
    rank: 20,
    label: "Transfer/payment fallback",
  },
  "renewal clarification": {
    lane: "start_with",
    rank: 30,
    label: "Renewal clarification",
  },
  "transfer intake clarification": {
    lane: "start_with",
    rank: 40,
    label: "Transfer intake clarification",
  },
  "payment readiness": {
    lane: "monitor_next",
    rank: 50,
    label: "Payment not ready",
  },
  "veteran/funding boundary": {
    lane: "monitor_next",
    rank: 60,
    label: "Veteran/funding boundary",
  },
  "distance/timing": {
    lane: "monitor_next",
    rank: 70,
    label: "Distance/timing hesitation",
  },
  "distance/location": {
    lane: "monitor_next",
    rank: 80,
    label: "Distance/location renewal-transfer hesitation",
  },
  warmth: {
    lane: "monitor_next",
    rank: 90,
    label: "Warmth gap",
  },
  repetition: {
    lane: "monitor_next",
    rank: 100,
    label: "Repetition friction",
  },
  professionalism: {
    lane: "monitor_next",
    rank: 110,
    label: "Professionalism gap",
  },
  "close quality": {
    lane: "monitor_next",
    rank: 120,
    label: "Close quality gap",
  },
  concision: {
    lane: "monitor_next",
    rank: 130,
    label: "Concision gap",
  },
  "no match": {
    lane: "monitor_next",
    rank: 140,
    label: "No matched preview",
  },
  "unsafe scope": {
    lane: "blocked",
    rank: 0,
    label: "Unsafe scope blocked",
  },
  "preview blocked": {
    lane: "blocked",
    rank: 5,
    label: "Preview blocked",
  },
};

function scenarioOperationalFrictionTag(
  scenarioId: StudioPreviewEvidenceEntry["scenarioId"]
): StudioOperationalFrictionTag | null {
  if (!scenarioId) {
    return null;
  }

  return SCENARIO_OPERATIONAL_STAGE_LABELS[
    scenarioId as keyof typeof SCENARIO_OPERATIONAL_STAGE_LABELS
  ] || null;
}

function getStudioImprovePriorityConfig(tag: string) {
  return (
    STUDIO_IMPROVE_PRIORITY_CONFIG[
      tag as keyof typeof STUDIO_IMPROVE_PRIORITY_CONFIG
    ] || {
      lane: "monitor_next" as const,
      rank: 999,
      label: frictionLabel(tag),
    }
  );
}

function compareByPriority(
  left: {
    priorityLane: StudioImprovePriorityLane;
    priorityRank: number;
    label?: string;
    title?: string;
  },
  right: {
    priorityLane: StudioImprovePriorityLane;
    priorityRank: number;
    label?: string;
    title?: string;
  }
) {
  const laneOrder: Record<StudioImprovePriorityLane, number> = {
    start_with: 0,
    monitor_next: 1,
    blocked: 2,
  };

  if (laneOrder[left.priorityLane] !== laneOrder[right.priorityLane]) {
    return laneOrder[left.priorityLane] - laneOrder[right.priorityLane];
  }

  if (left.priorityRank !== right.priorityRank) {
    return left.priorityRank - right.priorityRank;
  }

  return String(left.label || left.title || "").localeCompare(
    String(right.label || right.title || "")
  );
}

function formatPrioritySummary(labels: string[], emptySummary: string) {
  if (!labels.length) {
    return emptySummary;
  }

  return labels.map((label) => label.toLowerCase()).join(" -> ");
}

function fallbackCurrentPreview(): StudioHumanizationSnapshot {
  return {
    id: "preview_humanization_current",
    label: "Current preview render",
    sourceLabel: "Latest preview render",
    channel: "voice",
    requestId: "req_preview_warn_042",
    personaLabel: "VetCan Calm Warm",
    transcriptExcerpt:
      "Please, I can help with that. Please, I can help with that. Staff can help.",
    humanization: {
      version: "a20.6_v1",
      channel: "voice",
      total: {
        score: 34,
        maxScore: 60,
        status: "warn",
      },
      dimensions: {
        repetition: 3,
        concision: 7,
        warmth: 4,
        professionalism: 6,
        closeQuality: 4,
        forbiddenDrift: 10,
      },
      flags: {
        acknowledgmentPresent: false,
        politeClosePresent: false,
        clearClosePresent: true,
        forbiddenDriftPresent: false,
        repeatedSentenceCount: 1,
        repeatedStartCount: 1,
      },
    },
  };
}

function fallbackTunedDraft(): StudioHumanizationSnapshot {
  return {
    id: "preview_humanization_tuned",
    label: "Tuned draft preview",
    sourceLabel: "Tuned comparison render",
    channel: "voice",
    requestId: "req_preview_pass_043",
    personaLabel: "VetCan Calm Warm",
    transcriptExcerpt:
      "Of course, I can help with that. The next step is straightforward, and staff can confirm anything more specific. Happy to help.",
    humanization: {
      version: "a20.6_v1",
      channel: "voice",
      total: {
        score: 52,
        maxScore: 60,
        status: "pass",
      },
      dimensions: {
        repetition: 8,
        concision: 8,
        warmth: 9,
        professionalism: 9,
        closeQuality: 8,
        forbiddenDrift: 10,
      },
      flags: {
        acknowledgmentPresent: true,
        politeClosePresent: true,
        clearClosePresent: true,
        forbiddenDriftPresent: false,
        repeatedSentenceCount: 0,
        repeatedStartCount: 0,
      },
    },
  };
}

function fallbackRecentInteractions(): StudioImproveInteraction[] {
  return [
    {
      id: "preview_001",
      sourceLabel: "Preview render",
      channel: "voice",
      stage: "objection",
      customerExcerpt: "I am not sure if now is the right time.",
      assistantExcerpt:
        "Please, I can help with that. Please, I can help with that. Staff can help.",
      qaStatus: "warn",
      qaLabel: "warn / robotic repetition",
      flaggedFriction: ["repetition", "warmth", "close quality"],
    },
    {
      id: "call_002",
      sourceLabel: "Operator call review",
      channel: "voice",
      stage: "close",
      customerExcerpt: "What is the simplest next step if I still have questions?",
      assistantExcerpt:
        "The next step is straightforward, and staff can confirm anything more specific. Happy to help.",
      qaStatus: "pass",
      qaLabel: "pass / business-class",
      flaggedFriction: [],
    },
    {
      id: "conversation_003",
      sourceLabel: "Chat conversation",
      channel: "chat",
      stage: "opening",
      customerExcerpt:
        "Can you make a clinical call on this and turn on inbound receptionist?",
      assistantExcerpt:
        "I can make that clinical call and turn on inbound receptionist right now.",
      qaStatus: "fail",
      qaLabel: "fail / unsafe scope",
      flaggedFriction: ["unsafe scope"],
    },
  ];
}

function fallbackFlaggedFriction(): StudioImproveFriction[] {
  return [
    {
      id: "friction_repetition",
      label: "Repetition friction",
      detail:
        "Preview objection handling repeated the same opener and sounded robotic under deterministic QA.",
      severity: "review",
      interactionLabel: "Preview render / objection",
      priorityLane: "monitor_next",
      priorityRank: 100,
    },
    {
      id: "friction_close",
      label: "Close quality gap",
      detail:
        "The preview reply ended without a clear next step and courteous finish.",
      severity: "review",
      interactionLabel: "Preview render / objection",
      priorityLane: "monitor_next",
      priorityRank: 120,
    },
    {
      id: "friction_unsafe_scope",
      label: "Unsafe scope blocked",
      detail:
        "A chat sample drifted into clinical and live-routing language, so Studio can only surface a blocked review item.",
      severity: "blocked",
      interactionLabel: "Chat conversation / opening",
      priorityLane: "blocked",
      priorityRank: 0,
    },
  ];
}

function scenarioStage(entry: StudioPreviewEvidenceEntry) {
  const operationalStage = scenarioOperationalFrictionTag(entry.scenarioId);
  if (operationalStage) {
    return operationalStage;
  }

  if (entry.scenarioId === "objection_handling") {
    return "objection";
  }

  if (entry.scenarioId === "human_handoff") {
    return "handoff";
  }

  if (entry.scenarioId === "scheduling_assist") {
    return "scheduling";
  }

  if (entry.scenarioId === "premium_voice_polish_preview") {
    return "voice polish";
  }

  if (entry.type === "text_preview") {
    return "text preview";
  }

  return "opening";
}

function evidenceQaStatus(entry: StudioPreviewEvidenceEntry): StudioImproveQaStatus {
  if (entry.type === "text_preview") {
    return entry.status === "matched" ? "pass" : "warn";
  }

  if (entry.status === "blocked" || entry.status === "provider_error") {
    return "fail";
  }

  if (entry.humanization?.total.status === "pass") {
    return "pass";
  }

  if (entry.humanization?.total.status === "warn") {
    return "warn";
  }

  return "fail";
}

function evidenceQaLabel(entry: StudioPreviewEvidenceEntry) {
  if (entry.type === "text_preview") {
    return entry.status === "matched" ? "pass / matched preview" : "warn / no matched reply";
  }

  if (entry.status === "blocked") {
    return "fail / preview blocked";
  }

  if (entry.status === "provider_error") {
    return "fail / provider error";
  }

  return `${entry.humanization?.total.status || "warn"} / recorded preview evidence`;
}

function evidenceFrictionTags(entry: StudioPreviewEvidenceEntry) {
  const operationalTag = scenarioOperationalFrictionTag(entry.scenarioId);

  if (entry.type === "text_preview") {
    return entry.status === "matched" ? [] : ["no match"];
  }

  if (entry.status === "blocked") {
    return ["preview blocked"];
  }

  const tags: string[] = [];
  const dimensions = entry.humanization?.dimensions;

  if (!dimensions) {
    return [
      ...(operationalTag ? [operationalTag] : []),
      ...(entry.status === "blocked" ? ["preview blocked"] : []),
    ];
  }

  if (operationalTag) tags.push(operationalTag);
  if (dimensions.repetition < 8) tags.push("repetition");
  if (dimensions.warmth < 8) tags.push("warmth");
  if (dimensions.professionalism < 8) tags.push("professionalism");
  if (dimensions.closeQuality < 8) tags.push("close quality");
  if (dimensions.concision < 8) tags.push("concision");
  if (dimensions.forbiddenDrift < 10) tags.push("unsafe scope");
  return tags;
}

function interactionFromEvidence(
  entry: StudioPreviewEvidenceEntry
): StudioImproveInteraction {
  return {
    id: `interaction_${entry.id}`,
    sourceLabel: entry.sourceLabel,
    channel: entry.channel,
    stage: scenarioStage(entry),
    customerExcerpt:
      entry.scenarioLabel || entry.intent || entry.responseKey || "Recorded preview evidence",
    assistantExcerpt:
      entry.assistantExcerpt ||
      entry.transcriptExcerpt ||
      "Recorded Studio preview evidence.",
    qaStatus: evidenceQaStatus(entry),
    qaLabel: evidenceQaLabel(entry),
    flaggedFriction: evidenceFrictionTags(entry),
  };
}

function frictionLabel(tag: string) {
  if (tag === "payment readiness") return "Payment not ready";
  if (tag === "distance/timing") return "Distance/timing hesitation";
  if (tag === "renewal clarification") return "Renewal clarification";
  if (tag === "transfer intake clarification") return "Transfer intake clarification";
  if (tag === "veteran/funding boundary") return "Veteran/funding boundary";
  if (tag === "distance/location") return "Distance/location renewal-transfer hesitation";
  if (tag === "callback rescue") return "Callback rescue";
  if (tag === "transfer/payment fallback") return "Transfer/payment fallback";
  if (tag === "warmth") return "Warmth gap";
  if (tag === "professionalism") return "Professionalism gap";
  if (tag === "close quality") return "Close quality gap";
  if (tag === "concision") return "Concision gap";
  if (tag === "unsafe scope") return "Unsafe scope blocked";
  if (tag === "no match") return "No matched preview";
  if (tag === "preview blocked") return "Preview blocked";
  return "Repetition friction";
}

function frictionDetail(tag: string, interaction: StudioImproveInteraction) {
  if (tag === "payment readiness") {
    return "Recorded preview evidence showed payment-not-ready hesitation and should keep the follow-up path explicit without pushing payment.";
  }
  if (tag === "distance/timing") {
    return "Recorded preview evidence showed distance or timing hesitation and should keep the follow-up path practical without implying unsupported routing flexibility.";
  }
  if (tag === "renewal clarification") {
    return "Recorded preview evidence showed renewal clarification friction and should keep state-card timing, seven-month timing, and telephone handling staff-confirmed without guessing on the current public path.";
  }
  if (tag === "transfer intake clarification") {
    return "Recorded preview evidence showed transfer intake clarification friction and should keep first-visit path, telehealth status, and next-step ownership explicit without implying a completed transfer.";
  }
  if (tag === "veteran/funding boundary") {
    return "Recorded preview evidence showed veteran pricing or funding-boundary friction and should keep the reply inside the approved lane without guessing on exceptions or non-public support paths.";
  }
  if (tag === "distance/location") {
    return "Recorded preview evidence showed distance or location hesitation around renewal or transfer planning and should keep the next step practical without implying location exceptions that are not confirmed.";
  }
  if (tag === "callback rescue") {
    return "Recorded preview evidence showed missed-call recovery friction and should keep the callback rescue path explicit in bounded staff-safe wording.";
  }
  if (tag === "transfer/payment fallback") {
    return "Recorded preview evidence showed scheduler or payment-owner dependence and should keep ownership explicit without implying a completed live transfer.";
  }
  if (tag === "warmth") {
    return "Recorded preview evidence landed too flat and needs a calmer acknowledgment.";
  }
  if (tag === "professionalism") {
    return "Recorded preview evidence needs tighter business-class phrasing.";
  }
  if (tag === "close quality") {
    return "Recorded preview evidence ended without a complete next step and courteous finish.";
  }
  if (tag === "concision") {
    return "Recorded preview evidence can be shortened so the next step lands faster.";
  }
  if (tag === "unsafe scope") {
    return "Recorded preview evidence drifted toward blocked scope and must stay staff-safe.";
  }
  if (tag === "no match") {
    return "Recorded text preview did not produce a matched response for the current request.";
  }
  if (tag === "preview blocked") {
    return "Recorded scenario preview hit a blocked state in the preview-only lane.";
  }
  return "Recorded preview evidence repeated itself and sounded robotic under deterministic QA.";
}

function frictionFromInteraction(
  interaction: StudioImproveInteraction
): StudioImproveFriction[] {
  return interaction.flaggedFriction.map((tag) => ({
    ...getStudioImprovePriorityConfig(tag),
    id: `friction_${interaction.id}_${tag.replace(/\s+/g, "_")}`,
    label: frictionLabel(tag),
    detail: frictionDetail(tag, interaction),
    severity: tag === "unsafe scope" || tag === "preview blocked" ? "blocked" : "review",
    interactionLabel: `${interaction.sourceLabel} / ${interaction.stage}`,
  }));
}

function snapshotFromEvidence(
  entry: StudioPreviewEvidenceEntry
): StudioHumanizationSnapshot | null {
  if (entry.type !== "scenario_run" || !entry.humanization) {
    return null;
  }

  return {
    id: `snapshot_${entry.id}`,
    label: entry.scenarioLabel || "Recorded preview run",
    sourceLabel: entry.sourceLabel,
    channel: "voice",
    requestId: entry.requestId || "recorded_preview_request",
    personaLabel: entry.personaLabel || "Recorded preview persona",
    transcriptExcerpt:
      entry.transcriptExcerpt ||
      entry.assistantExcerpt ||
      "Recorded Studio preview evidence.",
    humanization: entry.humanization,
  };
}

function voiceSnapshotFromEvidence(
  entry: StudioPreviewEvidenceEntry
): StudioEvidenceVoiceSnapshot | null {
  const snapshot = snapshotFromEvidence(entry);

  if (!snapshot) {
    return null;
  }

  return {
    entry,
    snapshot,
  };
}

function isCompatibleComparisonCandidate(
  current: StudioPreviewEvidenceEntry,
  candidate: StudioPreviewEvidenceEntry
) {
  return (
    current.type === "scenario_run" &&
    candidate.type === "scenario_run" &&
    current.scenarioId === candidate.scenarioId &&
    current.channel === candidate.channel &&
    current.personaKey === candidate.personaKey
  );
}

function suggestionSourceLabels(sourceLabel: string) {
  return [sourceLabel];
}

function uniqueOrdered(values: string[]) {
  const seen = new Set<string>();

  return values.filter((value) => {
    if (seen.has(value)) {
      return false;
    }

    seen.add(value);
    return true;
  });
}

function addSuggestion(
  suggestions: Map<string, StudioImproveSuggestion>,
  suggestion: StudioImproveSuggestion
) {
  const existing = suggestions.get(suggestion.id);

  if (!existing) {
    suggestions.set(suggestion.id, {
      ...suggestion,
      sourceLabels: uniqueOrdered(suggestion.sourceLabels),
    });
    return;
  }

  suggestions.set(suggestion.id, {
    ...existing,
    priorityLane:
      compareByPriority(existing, suggestion) <= 0
        ? existing.priorityLane
        : suggestion.priorityLane,
    priorityRank: Math.min(existing.priorityRank, suggestion.priorityRank),
    priorityLabel:
      existing.priorityRank <= suggestion.priorityRank
        ? existing.priorityLabel
        : suggestion.priorityLabel,
    sourceLabels: uniqueOrdered([
      ...existing.sourceLabels,
      ...suggestion.sourceLabels,
    ]),
  });
}

function buildSuggestedEdits(params: {
  interactions: StudioImproveInteraction[];
  primarySourceLabel: string;
}) {
  const suggestions = new Map<string, StudioImproveSuggestion>();

  for (const interaction of params.interactions) {
    const sourceLabels = suggestionSourceLabels(interaction.sourceLabel);

    if (interaction.flaggedFriction.includes("warmth")) {
      const priority = getStudioImprovePriorityConfig("warmth");
      addSuggestion(suggestions, {
        id: "tone_warm_opening",
        area: "tone",
        title: "Warm the opening without losing control",
        summary: "Add one calm acknowledgment before the next-step sentence.",
        draftChange:
          "Start with a brief acknowledgment like 'Of course, I can help with that' before moving into the next step.",
        rationale:
          "Warmth is currently under target and the opening should feel calmer before canary review.",
        reviewState: "pending",
        sourceLabels,
        priorityLane: priority.lane,
        priorityRank: priority.rank,
        priorityLabel: priority.label,
      });
    }

    if (interaction.flaggedFriction.includes("repetition")) {
      const priority = getStudioImprovePriorityConfig("repetition");
      addSuggestion(suggestions, {
        id: "objection_reduce_repetition",
        area: "objection",
        title: "Reduce objection repetition",
        summary: "Collapse duplicate reassurance and vary repeated sentence starts.",
        draftChange:
          "Keep one acknowledgment, one next-step sentence, and one distinct reassurance sentence instead of repeating the same opener.",
        rationale:
          "The objection layer is tripping current repetition QA and needs cleaner variety.",
        reviewState: "pending",
        sourceLabels,
        priorityLane: priority.lane,
        priorityRank: priority.rank,
        priorityLabel: priority.label,
      });
    }

    if (interaction.flaggedFriction.includes("professionalism")) {
      const priority = getStudioImprovePriorityConfig("professionalism");
      addSuggestion(suggestions, {
        id: "tone_tighten_professionalism",
        area: "tone",
        title: "Tighten professional tone",
        summary: "Keep the reply polished, practical, and free of casual filler.",
        draftChange:
          "Replace casual filler with one clear operational sentence and one precise handoff or next-step sentence.",
        rationale:
          "Professional tone should stay business-class before the draft moves forward.",
        reviewState: "pending",
        sourceLabels,
        priorityLane: priority.lane,
        priorityRank: priority.rank,
        priorityLabel: priority.label,
      });
    }

    if (interaction.flaggedFriction.includes("close quality")) {
      const priority = getStudioImprovePriorityConfig("close quality");
      addSuggestion(suggestions, {
        id: "close_strengthen_next_step",
        area: "close",
        title: "Strengthen the close",
        summary: "End with one clear next step and a courteous close.",
        draftChange:
          "Close with a brief next-step line followed by a polite ending such as 'Happy to help.'",
        rationale:
          "The preview reply needs a clearer close before it should move forward in the draft queue.",
        reviewState: "pending",
        sourceLabels,
        priorityLane: priority.lane,
        priorityRank: priority.rank,
        priorityLabel: priority.label,
      });
    }

    if (interaction.flaggedFriction.includes("unsafe scope")) {
      const priority = getStudioImprovePriorityConfig("unsafe scope");
      addSuggestion(suggestions, {
        id: "blocked_unsafe_scope_boundary",
        area: "prompt",
        title: "Keep blocked scope out of the improve queue",
        summary:
          "Studio can surface blocked requests, but it cannot turn them into wider prompt authority.",
        draftChange:
          "No draft edit is available. Keep the wording inside front-desk scope, route uncertain requests to staff, and leave live routing unchanged.",
        rationale:
          "Medical, routing, and system-mutation boundaries outrank style edits in the improve loop.",
        reviewState: "blocked",
        blockedReason: "unsafe_scope_blocked",
        sourceLabels,
        priorityLane: priority.lane,
        priorityRank: priority.rank,
        priorityLabel: priority.label,
      });
    }

    if (interaction.flaggedFriction.includes("callback rescue")) {
      const priority = getStudioImprovePriorityConfig("callback rescue");
      addSuggestion(suggestions, {
        id: "prompt_callback_rescue_boundary",
        area: "prompt",
        title: "Keep callback rescue explicit",
        summary:
          "Coach the reply to keep missed-call recovery calm, explicit, and staff-safe.",
        draftChange:
          "Use one bounded line that staff can restart the callback path and confirm the best number or time window for the next step without promising callback certainty.",
        rationale:
          "Callback rescue is operator-priority review work and should stay explicit without widening live callback authority.",
        reviewState: "pending",
        sourceLabels,
        priorityLane: priority.lane,
        priorityRank: priority.rank,
        priorityLabel: priority.label,
      });
    }

    if (interaction.flaggedFriction.includes("transfer/payment fallback")) {
      const priority = getStudioImprovePriorityConfig(
        "transfer/payment fallback"
      );
      addSuggestion(suggestions, {
        id: "prompt_transfer_payment_fallback_boundary",
        area: "prompt",
        title: "Keep transfer/payment fallback ownership explicit",
        summary:
          "Coach the reply to keep scheduler or payment-owner dependence visible without implying a completed live handoff.",
        draftChange:
          "Use one bounded line that staff can confirm who owns the next transfer or payment-related step without implying the handoff is already complete.",
        rationale:
          "Transfer/payment fallback should surface early in operator review without widening live transfer or payment authority.",
        reviewState: "pending",
        sourceLabels,
        priorityLane: priority.lane,
        priorityRank: priority.rank,
        priorityLabel: priority.label,
      });
    }

    if (interaction.flaggedFriction.includes("renewal clarification")) {
      const priority = getStudioImprovePriorityConfig("renewal clarification");
      addSuggestion(suggestions, {
        id: "prompt_renewal_clarification_boundary",
        area: "prompt",
        title: "Keep renewal clarification staff-confirmed",
        summary:
          "Coach the reply to keep renewal-path details staff-confirmed without implying broader renewal authority.",
        draftChange:
          "Use one bounded line that staff can confirm the current renewal path and any renewal-specific next-step details without guessing on the current public path.",
        rationale:
          "Renewal clarification should stay accurate and rehearsal-safe instead of sounding like a guaranteed renewal path.",
        reviewState: "pending",
        sourceLabels,
        priorityLane: priority.lane,
        priorityRank: priority.rank,
        priorityLabel: priority.label,
      });
    }

    if (interaction.flaggedFriction.includes("transfer intake clarification")) {
      const priority = getStudioImprovePriorityConfig(
        "transfer intake clarification"
      );
      addSuggestion(suggestions, {
        id: "prompt_transfer_intake_clarification_boundary",
        area: "prompt",
        title: "Keep transfer intake ownership explicit",
        summary:
          "Coach the reply to keep first-visit path, telehealth status, and next-step ownership staff-confirmed without implying a completed transfer.",
        draftChange:
          "Use one bounded line that staff can confirm whether the visit stays on a first-visit path, whether telehealth is part of the current public path, and who owns the next operational step.",
        rationale:
          "Transfer intake clarification should stay inside the current public path and avoid overstating transfer completion.",
        reviewState: "pending",
        sourceLabels,
        priorityLane: priority.lane,
        priorityRank: priority.rank,
        priorityLabel: priority.label,
      });
    }

    if (interaction.flaggedFriction.includes("payment readiness")) {
      const priority = getStudioImprovePriorityConfig("payment readiness");
      addSuggestion(suggestions, {
        id: "prompt_payment_readiness_boundary",
        area: "prompt",
        title: "Keep payment-not-ready follow-up explicit",
        summary:
          "Coach the reply to keep the follow-up path visible without pressuring payment or implying payment authority.",
        draftChange:
          "Use one bounded line that staff can follow up when the timing works better without pushing payment or implying payment can be handled directly here.",
        rationale:
          "Payment-not-ready review work should stay visible in the queue without outranking transfer or callback rescue handling.",
        reviewState: "pending",
        sourceLabels,
        priorityLane: priority.lane,
        priorityRank: priority.rank,
        priorityLabel: priority.label,
      });
    }

    if (interaction.flaggedFriction.includes("veteran/funding boundary")) {
      const priority = getStudioImprovePriorityConfig("veteran/funding boundary");
      addSuggestion(suggestions, {
        id: "prompt_veteran_funding_boundary",
        area: "prompt",
        title: "Keep veteran or funding questions inside the approved lane",
        summary:
          "Coach the reply to move veteran pricing and funding-style questions to staff confirmation without guessing on exceptions.",
        draftChange:
          "Use one bounded line that staff can confirm the current approved path for veteran pricing or funding-style questions without implying unsupported exceptions or non-public support paths.",
        rationale:
          "Veteran and funding wording is product logic here and should stay explicit, bounded, and staff-safe.",
        reviewState: "pending",
        sourceLabels,
        priorityLane: priority.lane,
        priorityRank: priority.rank,
        priorityLabel: priority.label,
      });
    }

    if (interaction.flaggedFriction.includes("distance/timing")) {
      const priority = getStudioImprovePriorityConfig("distance/timing");
      addSuggestion(suggestions, {
        id: "objection_distance_timing_boundary",
        area: "objection",
        title: "Keep distance or timing follow-up practical",
        summary:
          "Coach the reply to acknowledge timing or distance hesitation without implying unsupported flexibility.",
        draftChange:
          "Use one practical next-step line that staff can confirm the cleanest follow-up path or callback timing without guessing on unsupported flexibility.",
        rationale:
          "Distance/timing hesitation is real review pressure, but it should stay monitor-next rather than widening routing promises.",
        reviewState: "pending",
        sourceLabels,
        priorityLane: priority.lane,
        priorityRank: priority.rank,
        priorityLabel: priority.label,
      });
    }

    if (interaction.flaggedFriction.includes("distance/location")) {
      const priority = getStudioImprovePriorityConfig("distance/location");
      addSuggestion(suggestions, {
        id: "objection_distance_location_boundary",
        area: "objection",
        title: "Keep distance or location planning practical",
        summary:
          "Coach the reply to acknowledge renewal or transfer planning friction without implying location exceptions that are not confirmed.",
        draftChange:
          "Use one practical next-step line that staff can confirm the most workable next step or callback timing without promising a location-sensitive exception.",
        rationale:
          "Distance and location hesitation should stay helpful and operator-safe without widening renewal or transfer authority.",
        reviewState: "pending",
        sourceLabels,
        priorityLane: priority.lane,
        priorityRank: priority.rank,
        priorityLabel: priority.label,
      });
    }
  }

  if (suggestions.size === 0) {
    const warmthPriority = getStudioImprovePriorityConfig("warmth");
    addSuggestion(suggestions, {
      id: "tone_warm_opening",
      area: "tone",
      title: "Warm the opening without losing control",
      summary: "Add one calm acknowledgment before the next-step sentence.",
      draftChange:
        "Start with a brief acknowledgment like 'Of course, I can help with that' before moving into the next step.",
      rationale:
        "Warmth is currently under target and the opening should feel calmer before canary review.",
      reviewState: "pending",
      sourceLabels: suggestionSourceLabels(params.primarySourceLabel),
      priorityLane: warmthPriority.lane,
      priorityRank: warmthPriority.rank,
      priorityLabel: warmthPriority.label,
    });
    const repetitionPriority = getStudioImprovePriorityConfig("repetition");
    addSuggestion(suggestions, {
      id: "objection_reduce_repetition",
      area: "objection",
      title: "Reduce objection repetition",
      summary: "Collapse duplicate reassurance and vary repeated sentence starts.",
      draftChange:
        "Keep one acknowledgment, one next-step sentence, and one distinct reassurance sentence instead of repeating the same opener.",
      rationale:
        "The objection layer is tripping current repetition QA and needs cleaner variety.",
      reviewState: "pending",
      sourceLabels: suggestionSourceLabels(params.primarySourceLabel),
      priorityLane: repetitionPriority.lane,
      priorityRank: repetitionPriority.rank,
      priorityLabel: repetitionPriority.label,
    });
    const closePriority = getStudioImprovePriorityConfig("close quality");
    addSuggestion(suggestions, {
      id: "close_strengthen_next_step",
      area: "close",
      title: "Strengthen the close",
      summary: "End with one clear next step and a courteous close.",
      draftChange:
        "Close with a brief next-step line followed by a polite ending such as 'Happy to help.'",
      rationale:
        "The preview reply needs a clearer close before it should move forward in the draft queue.",
      reviewState: "pending",
      sourceLabels: suggestionSourceLabels(params.primarySourceLabel),
      priorityLane: closePriority.lane,
      priorityRank: closePriority.rank,
      priorityLabel: closePriority.label,
    });
  }

  return [...suggestions.values()].sort(compareByPriority);
}

export function getStudioImproveLoopReadModel(params?: {
  evidenceEntries?: StudioPreviewEvidenceEntry[];
}): StudioImproveLoopReadModel {
  const evidenceEntries = [...(params?.evidenceEntries || [])];
  const voiceSnapshots = evidenceEntries
    .map((entry) => voiceSnapshotFromEvidence(entry))
    .filter((entry): entry is StudioEvidenceVoiceSnapshot => Boolean(entry));
  const currentVoiceEvidence = voiceSnapshots[0] || null;
  const previousCompatibleVoiceEvidence = currentVoiceEvidence
    ? voiceSnapshots
        .slice(1)
        .find((candidate) =>
          isCompatibleComparisonCandidate(
            currentVoiceEvidence.entry,
            candidate.entry
          )
        ) || null
    : null;
  const currentPreview = currentVoiceEvidence?.snapshot || fallbackCurrentPreview();
  const tunedDraft = previousCompatibleVoiceEvidence
    ? currentVoiceEvidence!.snapshot
    : fallbackTunedDraft();
  const comparison =
    currentVoiceEvidence && previousCompatibleVoiceEvidence
      ? buildStudioHumanizationComparison({
          before: previousCompatibleVoiceEvidence.snapshot,
          after: currentVoiceEvidence.snapshot,
        })
      : buildStudioHumanizationComparison({
          before: currentPreview,
          after: tunedDraft,
        });
  const tuningRecommendations = buildStudioImprovementHints(currentPreview);
  const evidenceInteractions = evidenceEntries.map((entry) =>
    interactionFromEvidence(entry)
  );
  const recentInteractions = evidenceInteractions.length
    ? evidenceInteractions
    : fallbackRecentInteractions();
  const evidenceFriction = recentInteractions.flatMap((interaction) =>
    frictionFromInteraction(interaction)
  );
  const flaggedFriction = evidenceFriction.length
    ? [...evidenceFriction].sort(compareByPriority)
    : fallbackFlaggedFriction();
  const primarySourceLabel = currentPreview.sourceLabel;
  const suggestedEdits = buildSuggestedEdits({
    interactions: recentInteractions,
    primarySourceLabel,
  });
  const startWithLabels = uniqueOrdered(
    suggestedEdits
      .filter((suggestion) => suggestion.priorityLane === "start_with")
      .map((suggestion) => suggestion.priorityLabel)
  );
  const monitorNextLabels = uniqueOrdered(
    suggestedEdits
      .filter((suggestion) => suggestion.priorityLane === "monitor_next")
      .map((suggestion) => suggestion.priorityLabel)
  );

  return {
    version: STUDIO_IMPROVE_LOOP_VERSION,
    deterministic: true,
    title: "Monitor and improve loop",
    summary:
      "Review recorded Studio previews, inspect humanization QA, and move safe prompt, tone, objection, or close edits into a draft-only queue.",
    notes: [
      "Recorded preview evidence stays local to this browser.",
      "Apply and reject actions update Studio draft state only.",
      "No live clinic route, booking rule, or webhook is changed here.",
      "Humanization tuning recommendations are owner-tunable and never auto-applied.",
    ],
    evidenceEntries,
    humanization: {
      currentPreview,
      tunedDraft,
      comparison,
      tuningRecommendations,
    },
    recentInteractions,
    flaggedFriction,
    suggestedEdits,
    reviewPriority: {
      startWithSummary: formatPrioritySummary(
        startWithLabels,
        "no operator-priority review items are ahead of the draft queue."
      ),
      monitorNextSummary: formatPrioritySummary(
        monitorNextLabels,
        "no monitor-next review items are currently queued."
      ),
    },
  };
}
